import { Action } from 'redux';
import { initializeState } from 'src/utils/Initial';
import { isType } from 'typescript-fsa';
import * as Actions from '../actions/profile';

/**
 * ユーザ情報画面のState型
 */
export interface IProfileState {
	readonly storedProfile: Actions.IProfile;
	readonly setStoredProfileAt?: Date;
	readonly editedProfile: Actions.IProfile;
	readonly countryMaster: { [code: string]: string };
	readonly timezoneMaster: { [code: string]: string };
	readonly loading: boolean;
	readonly langMaster: { [code: string]: string };
	readonly officeMaster: { [code: string]: string };
	readonly employeeCategoryMaster: { [code: string]: string };
	readonly occupationMaster: { [code: string]: string };
	readonly saveCompleteFlg: boolean;
	readonly needLogoutFlg: boolean;
	readonly emCooperationFlg: boolean;
}

/**
 * ユーザ情報画面のState初期値
 */
const initialState: IProfileState = {
	countryMaster: {},
	editedProfile: {
		addressEn: '',
		addressJa: '',
		addressZh: '',
		applyFlg: 0,
		birthday: '',
		careerMemoEn: '',
		careerMemoJa: '',
		careerMemoZh: '',
		certification: 0,
		countryCode: 'JP',
		createGroupFlg: 0,
		deleteImage: false,
		emCooperationMode: '',
		emUserSynFlg: '',
		email1: '',
		email2: '',
		employeeCategoryId: '',
		existsImageFile: false,
		familyNameEn: '',
		familyNameJa: '',
		familyNameKanaEn: '',
		familyNameKanaJa: '',
		familyNameKanaZh: '',
		familyNameZh: '',
		faxNumber: '',
		firstNameEn: '',
		firstNameJa: '',
		firstNameKanaEn: '',
		firstNameKanaJa: '',
		firstNameKanaZh: '',
		firstNameZh: '',
		groups: [],
		homePhone: '',
		idFlg: '0',
		lang: 'ja',
		lederUserId: '',
		lederUserName: '',
		loginId: '',
		noteEn: '',
		noteJa: '',
		noteZh: '',
		occupationId: '',
		officeId: '',
		orgs: [],
		primaryOrgId: '',
		seibetu: '0',
		timeZone: 'JST',
		userOrgs: [],
		workStartDate: '',
	},
	emCooperationFlg: false,
	employeeCategoryMaster: {},
	langMaster: {},
	loading: false,
	needLogoutFlg: false,
	occupationMaster: {},
	officeMaster: {},
	saveCompleteFlg: false,
	storedProfile: {
		addressEn: '',
		addressJa: '',
		addressZh: '',
		applyFlg: 0,
		birthday: '',
		careerMemoEn: '',
		careerMemoJa: '',
		careerMemoZh: '',
		certification: 0,
		countryCode: 'JP',
		createGroupFlg: 0,
		deleteImage: false,
		emCooperationMode: '',
		emUserSynFlg: '',
		email1: '',
		email2: '',
		employeeCategoryId: '',
		existsImageFile: false,
		familyNameEn: '',
		familyNameJa: '',
		familyNameKanaEn: '',
		familyNameKanaJa: '',
		familyNameKanaZh: '',
		familyNameZh: '',
		faxNumber: '',
		firstNameEn: '',
		firstNameJa: '',
		firstNameKanaEn: '',
		firstNameKanaJa: '',
		firstNameKanaZh: '',
		firstNameZh: '',
		groups: [],
		homePhone: '',
		idFlg: '0',
		lang: 'ja',
		lederUserId: '',
		lederUserName: '',
		loginId: '',
		noteEn: '',
		noteJa: '',
		noteZh: '',
		occupationId: '',
		officeId: '',
		orgs: [],
		primaryOrgId: '',
		seibetu: '0',
		timeZone: 'JST',
		userOrgs: [],
		workStartDate: '',
	},
	timezoneMaster: {},
};

/**
 * ユーザ情報画面用reducer
 * @param state State
 * @param action アクション
 */
const profile = (state: IProfileState, action: Action) => {
	if (!state) {
		// State初期化処理
		state = initialState;
		state = initializeState(state);
	}
	// 保存済みユーザ情報データ設定
	if (isType(action, Actions.setStoredProfile)) {
		return {
			...state,
			setStoredProfileAt: new Date(),
			storedProfile: convertNullToInitState(action.payload.value),
		};
	}

	// 編集中ユーザ情報データ設定
	if (isType(action, Actions.setEditedProfile)) {
		return {
			...state,
			editedProfile: convertNullToInitState(action.payload.value),
		};
	}

	// 国マスタ設定
	if (isType(action, Actions.setCountryMaster)) {
		return {
			...state,
			countryMaster: action.payload.value,
		};
	}

	// タイムゾーンマスタ設定
	if (isType(action, Actions.setTimezoneMaster)) {
		return {
			...state,
			timezoneMaster: action.payload.value,
		};
	}

	// 言語マスタ設定
	if (isType(action, Actions.setLangMaster)) {
		return {
			...state,
			langMaster: action.payload.value,
		};
	}

	// 事業所マスタ設定
	if (isType(action, Actions.setOfficeMaster)) {
		return {
			...state,
			officeMaster: action.payload.value,
		};
	}

	// 従業員区分マスタ設定
	if (isType(action, Actions.setEmployeeCategoryMaster)) {
		return {
			...state,
			employeeCategoryMaster: action.payload.value,
		};
	}

	// 職種マスタ設定
	if (isType(action, Actions.setOccupationMaster)) {
		return {
			...state,
			occupationMaster: action.payload.value,
		};
	}

	// ユーザ情報登録完了フラグ設定
	if (isType(action, Actions.setSaveProfileCompleteFlg)) {
		return {
			...state,
			needLogoutFlg: action.payload.needLogoutFlg,
			saveCompleteFlg: action.payload.saveCompleteFlg,
		};
	}

	// ユーザ情報登録完了Loadingフラグ設定
	if (isType(action, Actions.setSaveLoadingFlg)) {
		return {
			...state,
			loading: action.payload.loading,
		};
	}

	return state;
};

/**
 * null/undefinedを初期値に置き換える
 * @param p 変換対象
 */
const convertNullToInitState = (p: Actions.IProfile) => {
	for (const key in initialState.editedProfile) {
		if (p[key] === null || p[key] === undefined) {
			p[key] = initialState.editedProfile[key];
		}
	}
	return p;
};

export default profile;



// WEBPACK FOOTER //
// ./src/reducers/profile.ts